import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import Link from "next/link"
import { Image, ResponsiveImageType } from "react-datocms"
import Slider from "react-slick"
import colors from "@constants/colors"
import { body, bodyBold, h6 } from "@constants/typography"
import { StyledLink } from "@components/buttons/LinkButton"

interface FeaturedCardItem {
  id?: string
  cardImage?: ResponsiveImageType
  cardImageSrc?: string
  cardTitle?: string
  cardTitleColor?: string
  upperSubtitleText?: string
  upperSubtitleColor?: string
  lowerSubtitleText?: string
  lowerSubtitleColor?: string
  description?: string
  cardLink?: string
}

interface FeaturedCardWithTextProps {
  sectionTitle?: string
  buttonText?: string
  buttonLink?: string
  buttonTextColor?: string
  buttonBackgroundColor?: string
  textBlock?: string
  textBlockColor?: string
  cards?: FeaturedCardItem[]
}

const Section = styled.section`
  background: #f3f3f3;
  width: 100%;
`

const Container = styled.div`
  max-width: 1220px;
  margin: 0 auto;
  padding: 26px 12px 36px;

  ${up("laptop")} {
    padding: 42px 20px 48px;
  }
`

const Intro = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 0 auto 24px;
    max-width: 980px;
    text-align: center;
    white-space: pre-line;
    color: ${color ? `#${color}` : colors.obsidian};
    font-size: 31px;
    line-height: 1.38;

    ${up("laptop")} {
      margin-bottom: 40px;
      font-size: 46px;
    }
  `}
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`

const SectionTitle = styled.h2`
  ${h6}
  margin: 0;
  color: ${colors.obsidian};
  text-transform: uppercase;
  letter-spacing: 1.2px;
`

const SectionCta = styled(StyledLink)`
  ${css`
    width: auto;
    text-transform: uppercase;
    letter-spacing: 1.3px;
    font-size: 12px;
    height: 36px;
    padding: 0 16px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.22);
    gap: 10px;
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  ${up("laptop")} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }
`

const CardsSlider = styled(Slider)`
  position: relative;

  .slick-list {
    overflow: hidden;
  }

  .slick-track {
    display: flex;
  }

  .slick-slide > div {
    padding: 0 5px;
  }

  .slick-prev,
  .slick-next {
    display: flex !important;
    align-items: center;
    justify-content: center;
    z-index: 1;
    width: 42px;
    height: 42px;
    border-radius: 50px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  }

  .slick-prev {
    left: 8px;
  }

  .slick-next {
    right: 8px;
  }

  .slick-prev:before,
  .slick-next:before {
    content: "";
  }

  .slick-prev:after,
  .slick-next:after {
    content: "";
    width: 12px;
    height: 12px;
    border-right: 3px solid #e11a22;
    border-bottom: 3px solid #e11a22;
  }

  .slick-prev:after {
    transform: rotate(135deg);
  }

  .slick-next:after {
    transform: rotate(-45deg);
  }
`

const CardLink = styled.a`
  display: block;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #d7d7d7;
`

const Card = styled.article`
  position: relative;
  width: 100%;
  aspect-ratio: 0.75 / 1;
  overflow: hidden;

  .DatoImage {
    width: 100%;
    height: 100%;
  }

  .DatoImage img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.35s ease;
  }

  &:hover .DatoImage img {
    transform: scale(1.03);
  }

  &:hover .default-content {
    opacity: 0;
    transform: translateY(-8px);
  }

  &:hover .card-overlay {
    opacity: 1;
  }

  &:hover .card-description {
    opacity: 1;
    transform: translateY(0);
  }
`

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`

const DefaultContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 14px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.12) 0%,
    rgba(0, 0, 0, 0.2) 100%
  );
  transition: opacity 0.25s ease, transform 0.25s ease;
`

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.25s ease;
`

const CardDescription = styled.p`
  ${body}
  margin: 0;
  color: ${colors.snow};
  font-size: 14px;
  line-height: 1.5;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.25s ease, transform 0.25s ease;
`

const UpperSubtitle = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 0 0 4px;
    color: ${color ? `#${color}` : colors.snow};
    font-size: 12px;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    opacity: 0.9;
  `}
`

const CardTitle = styled.h3<{ color?: string }>`
  ${({ color }) => css`
    ${bodyBold}
    margin: 0;
    color: ${color ? `#${color}` : colors.snow};
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 21px;
    line-height: 1;

    ${up("laptop")} {
      font-size: 26px;
    }
  `}
`

const LowerSubtitle = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 6px 0 0;
    color: ${color ? `#${color}` : colors.snow};
    font-size: 9px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    opacity: 0.95;
  `}
`

const Arrow = styled.span`
  font-size: 16px;
`

const FeaturedCardWithText: React.FC<FeaturedCardWithTextProps> = ({
  sectionTitle,
  buttonText,
  buttonLink,
  buttonTextColor,
  buttonBackgroundColor,
  textBlock,
  textBlockColor,
  cards = [],
}) => {
  const resolvedButtonLink = buttonLink || "/seat-configurator"
  const shouldUseSlider = cards.length > 4

  const cardItems = cards.map((card, index) => {
    const key = card.id || `${card.cardTitle || "card"}-${index}`

    const cardContent = (
      <Card>
        {card.cardImage ? (
          <Image data={card.cardImage} className="DatoImage" />
        ) : card.cardImageSrc ? (
          <CardImage src={card.cardImageSrc} alt={card.cardTitle || ""} />
        ) : null}

        <DefaultContent className="default-content">
          {card.upperSubtitleText && (
            <UpperSubtitle color={card.upperSubtitleColor}>
              {card.upperSubtitleText}
            </UpperSubtitle>
          )}

          {card.cardTitle && (
            <CardTitle color={card.cardTitleColor}>{card.cardTitle}</CardTitle>
          )}

          {card.lowerSubtitleText && (
            <LowerSubtitle color={card.lowerSubtitleColor}>
              {card.lowerSubtitleText}
            </LowerSubtitle>
          )}
        </DefaultContent>

        <CardOverlay className="card-overlay">
          {card.description && (
            <CardDescription className="card-description">
              {card.description}
            </CardDescription>
          )}
        </CardOverlay>
      </Card>
    )

    if (!card.cardLink) {
      return <CardLink key={key}>{cardContent}</CardLink>
    }

    return (
      <Link key={key} href={card.cardLink} passHref prefetch={false}>
        <CardLink>{cardContent}</CardLink>
      </Link>
    )
  })

  return (
    <Section>
      <Container>
        {textBlock && <Intro color={textBlockColor}>{textBlock}</Intro>}

        <HeaderRow>
          <SectionTitle>{sectionTitle || "Shop by Category"}</SectionTitle>

          {buttonText && (
            <Link href={resolvedButtonLink} passHref prefetch={false}>
              <SectionCta
                $buttonColor={buttonBackgroundColor}
                $buttonTextColor={buttonTextColor}
              >
                {buttonText}
                <Arrow>&rsaquo;</Arrow>
              </SectionCta>
            </Link>
          )}
        </HeaderRow>

        {shouldUseSlider ? (
          <CardsSlider
            dots={false}
            arrows={true}
            infinite={cards.length > 4}
            speed={400}
            slidesToShow={4}
            slidesToScroll={1}
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                },
              },
              {
                breakpoint: 640,
                settings: {
                  slidesToShow: 1,
                },
              },
            ]}
          >
            {cardItems}
          </CardsSlider>
        ) : (
          <CardsGrid>{cardItems}</CardsGrid>
        )}
      </Container>
    </Section>
  )
}

export default FeaturedCardWithText
